import { useEffect } from "react";
import { Show, useForm } from "@refinedev/antd";
import { useList, useNavigation } from "@refinedev/core";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useMatch } from "react-router-dom";

import { AppTitle } from "../../../components";
import {
  TV_LEAGUES,
  TV_TARGET_LABELS,
  TvCollection,
  TvShow,
  TvSlide,
  TvSlideForm,
  toOptions,
  tvErrorNotification,
  tvSuccessNotification,
} from "../Lib";
import {
  MatchPicker,
  TvDelete,
  TvImageField,
  TvVideoField,
  VideoPicker,
} from "../Components";

export const TvSlideShow = () => {
  const route = useMatch("/tv/showcase/:id");
  const id = Number(route?.params.id) || undefined;
  const { push } = useNavigation();

  const { formProps, form, queryResult, formLoading, onFinish } =
    useForm<TvSlide>({
      resource: "tv-showcase",
      dataProviderName: "tvDataProvider",
      action: id ? "edit" : "create",
      id,
      redirect: false,
      ...tvSuccessNotification(id ? "Слайд сохранён" : "Слайд добавлен"),
      ...tvErrorNotification(),
      onMutationSuccess: ({ data }) => {
        if (!id && data?.id) push(`/tv/showcase/${data.id}`);
      },
    });
  const record = queryResult?.data?.data;

  useEffect(() => {
    if (record) {
      form.setFieldsValue({
        ...record,
        activeFrom: record.activeFrom ? dayjs(record.activeFrom) : null,
        activeTo: record.activeTo ? dayjs(record.activeTo) : null,
        noCover: !record.coverUrl,
      } as TvSlideForm);
    }
  }, [record, form]);

  const { data: shows } = useList<TvShow>({
    resource: "tv-shows",
    dataProviderName: "tvDataProvider",
    pagination: { mode: "off" },
  });
  const { data: collections } = useList<TvCollection>({
    resource: "tv-collections",
    dataProviderName: "tvDataProvider",
    pagination: { mode: "off" },
  });

  const targetType = Form.useWatch(["target", "type"], form);
  const targetLeagueId = Form.useWatch(["target", "leagueId"], form);
  const targetShowId = Form.useWatch(["target", "showId"], form);
  const title: string = Form.useWatch("title", form) ?? "";
  const subtitle: string = Form.useWatch("subtitle", form) ?? "";
  const coverUrl: string | null | undefined = Form.useWatch("coverUrl", form);

  const submit = (v: TvSlideForm) =>
    onFinish({
      // слайды только для главной: страницы лиг витрину не показывают (выбор места убран)
      leagueId: null,
      title: v.title,
      subtitle: v.subtitle?.trim() || null,
      coverUrl: v.coverUrl || null,
      videoUrl: v.videoUrl || null,
      buttonText: v.buttonText || null,
      target: v.target,
      audience: v.audience,
      hideAfterMatchEnd: v.hideAfterMatchEnd,
      activeFrom: v.activeFrom ? v.activeFrom.toISOString() : null,
      activeTo: v.activeTo ? v.activeTo.toISOString() : null,
      sortOrder: v.sortOrder,
      isActive: v.isActive,
    } as unknown as TvSlide);

  return (
    <Show
      isLoading={formLoading}
      title={
        <AppTitle isLoading={formLoading} canGoBack>
          {id ? record?.title ?? "Слайд" : "Новый слайд"}
        </AppTitle>
      }
      breadcrumb={false}
      goBack={false}
      footerButtons={null}
      headerButtons={() =>
        id && record ? (
          <TvDelete
            resource="tv-showcase"
            id={id}
            name={record.title}
            entityAccusative="слайд"
            redirectPath="/tv/showcase"
            size="middle"
          />
        ) : null
      }
    >
      <Form<TvSlideForm>
        {...formProps}
        layout="vertical"
        onFinish={submit}
        initialValues={{
          audience: "ALL",
          hideAfterMatchEnd: true,
          isActive: true,
          sortOrder: 0,
          buttonText: "Смотреть",
          target: { type: "VIDEO" },
        }}
      >
        <Row gutter={32}>
          <Col xs={24} xl={14}>
            <Form.Item
              name="title"
              label={`Заголовок · ${title.length}/40`}
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Укажите заголовок",
                },
                { max: 40, message: "Не длиннее 40 символов" },
              ]}
            >
              <Input
                maxLength={40}
                placeholder="Матч тура: «Урал» — «Торпедо»"
              />
            </Form.Item>
            <Form.Item
              name="subtitle"
              label={`Подзаголовок · ${subtitle.length}/200`}
              extra="Строка под заголовком на слайде. Пусто — без неё"
              rules={[{ max: 200, message: "Не длиннее 200 символов" }]}
            >
              <Input.TextArea
                maxLength={200}
                autoSize={{ minRows: 2, maxRows: 4 }}
                placeholder="Центральная игра тура: борьба за путёвку в РПЛ. Прямой эфир с комментарием"
              />
            </Form.Item>
            <Form.Item
              name="buttonText"
              label="Текст кнопки"
              extra="Пусто — без кнопки, кликается весь слайд"
              rules={[{ max: 32 }]}
            >
              <Input maxLength={32} />
            </Form.Item>

            <Typography.Title level={5}>Куда ведёт слайд</Typography.Title>
            <Form.Item
              name={["target", "type"]}
              label="Цель"
              rules={[{ required: true }]}
            >
              <Select
                options={toOptions(TV_TARGET_LABELS)}
                onChange={() =>
                  form.setFieldValue("target", {
                    type: form.getFieldValue(["target", "type"]),
                  })
                }
              />
            </Form.Item>
            {targetType === "VIDEO" && (
              <Form.Item
                name={["target", "videoId"]}
                label="Видео"
                rules={[{ required: true, message: "Выберите видео" }]}
              >
                <VideoPicker />
              </Form.Item>
            )}
            {(targetType === "MATCH" || targetType === "LEAGUE") && (
              <Form.Item
                name={["target", "leagueId"]}
                label="Лига"
                rules={[{ required: true, message: "Выберите лигу" }]}
              >
                <Select
                  options={TV_LEAGUES}
                  onChange={() =>
                    form.setFieldValue(["target", "matchId"], undefined)
                  }
                />
              </Form.Item>
            )}
            {targetType === "MATCH" && (
              <Form.Item
                name={["target", "matchId"]}
                label="Матч"
                rules={[{ required: true, message: "Выберите матч" }]}
              >
                <MatchPicker leagueId={targetLeagueId} />
              </Form.Item>
            )}
            {targetType === "SHOW" && (
              <Form.Item
                name={["target", "showId"]}
                label="Шоу или спецпроект"
                rules={[{ required: true, message: "Выберите шоу" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={(shows?.data ?? []).map((s) => ({
                    value: s.id,
                    label: s.title,
                  }))}
                  onChange={() =>
                    form.setFieldValue(["target", "videoId"], undefined)
                  }
                />
              </Form.Item>
            )}
            {targetType === "SHOW" && (
              <Form.Item
                name={["target", "videoId"]}
                label="Выпуск для кнопки «Смотреть»"
                extra="Пусто — последний выпуск шоу, обновляется сам. «Все выпуски» ведёт на страницу шоу"
              >
                <VideoPicker
                  showId={targetShowId}
                  placeholder="Последний выпуск"
                />
              </Form.Item>
            )}
            {targetType === "COLLECTION" && (
              <Form.Item
                name={["target", "collectionId"]}
                label="Подборка"
                rules={[{ required: true, message: "Выберите подборку" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={(collections?.data ?? []).map((c) => ({
                    value: c.id,
                    label: c.title,
                  }))}
                />
              </Form.Item>
            )}
            {targetType === "URL" && (
              <Form.Item
                name={["target", "url"]}
                label="Ссылка"
                rules={[
                  { required: true, message: "Укажите ссылку" },
                  {
                    pattern: /^https?:\/\//,
                    message: "Ссылка должна начинаться с http:// или https://",
                  },
                ]}
              >
                <Input placeholder="https://fnl.pro/…" />
              </Form.Item>
            )}
            {targetType === "MATCH" && (
              <Form.Item
                name="hideAfterMatchEnd"
                label="Убрать после финального свистка"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            )}

            <Typography.Title level={5}>Показ</Typography.Title>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="activeFrom"
                  label="Показывать с"
                  extra="Пусто — сразу"
                >
                  <DatePicker
                    showTime
                    format="DD.MM.YYYY HH:mm"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="activeTo"
                  label="Показывать до"
                  extra="Пусто — без срока"
                  dependencies={["activeFrom"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator: (_, value) => {
                        const from = getFieldValue("activeFrom");
                        return value && from && !from.isBefore(value)
                          ? Promise.reject(
                              new Error("Окончание должно быть позже начала")
                            )
                          : Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    showTime
                    format="DD.MM.YYYY HH:mm"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="audience" label="Кому">
                  <Select
                    options={[
                      { value: "ALL", label: "Всем" },
                      { value: "ANONYMOUS", label: "Только гостям" },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="sortOrder"
                  label="Порядок"
                  extra="Меньше — раньше"
                >
                  <InputNumber style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="isActive"
                  label="Включён"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col xs={24} xl={10}>
            <Form.Item
              name="coverUrl"
              label="Обложка 16:9"
              extra="1920×1080 px. Можно без фото — на сайте будет фон лиги"
            >
              <TvImageField label="Обложка слайда" />
            </Form.Item>
            {/* без фото — только осознанно: галочка, чтобы не опубликовать слайд без обложки случайно (13352) */}
            {!coverUrl && (
              <Form.Item
                name="noCover"
                valuePropName="checked"
                dependencies={["coverUrl"]}
                rules={[
                  {
                    validator: (_, checked) =>
                      checked || form.getFieldValue("coverUrl")
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "Загрузите обложку или отметьте «Опубликовать без фото»"
                            )
                          ),
                  },
                ]}
              >
                <Checkbox>Опубликовать без фото</Checkbox>
              </Form.Item>
            )}
            <Form.Item
              name="videoUrl"
              label="Видео фона"
              extra="Необязательно. Крутится на слайде без звука по кругу вместо обложки"
            >
              <TvVideoField />
            </Form.Item>
          </Col>
        </Row>
        <Button type="primary" htmlType="submit" loading={formLoading}>
          {id ? "Сохранить" : "Добавить слайд"}
        </Button>
      </Form>
    </Show>
  );
};
